import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, DestroyRef, ElementRef, OnInit, inject } from '@angular/core';
import { combineLatest, filter, forkJoin, of, switchMap } from 'rxjs';

import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FeatureFlags } from '@app/_models/applicationConfigDto';
import { CatalogViewDto } from '@app/_models/catalogViewDto';
import { EmployeeGroupDto } from '@app/_models/employeeGroupDto';
import { AccountService } from '@app/_services/account.service';
import { ApplicationService } from '@app/_services/application.service';
import { CatalogViewService } from '@app/_services/catalogView.service';
import { ContractService } from '@app/_services/contract.service';
import { EmployeeService } from '@app/_services/employee.service';
import { ErrorHandlerService } from '@app/_services/errorHandler.service';
import { UserContractService } from '@app/_services/userContract.service';
import { TranslateService, TranslationObject } from '@ngx-translate/core';
import { groupBy, sortBy } from 'lodash-es';
import { NgAceSubmenu } from '../../_ace/directives/ace-submenu.directive';
import { SidebarItem } from './sidebar-item';
import sidebarData from './sidebar.json';
import { SidebarSubmenuComponent } from './submenu.component';

@Component({
  selector: 'app-sidebar',
  templateUrl: './sidebar.component.html',
  styleUrls: ['./sidebar.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SidebarSubmenuComponent, NgAceSubmenu],
})
export class SidebarComponent implements OnInit {
  private readonly el = inject(ElementRef);
  private readonly accountService = inject(AccountService);
  private readonly catalogViewService = inject(CatalogViewService);
  private readonly contractService = inject(ContractService);
  private readonly employeeService = inject(EmployeeService);
  private readonly userContractService = inject(UserContractService);
  private readonly errorHandler = inject(ErrorHandlerService);
  private readonly translationService = inject(TranslateService);
  private readonly applicationService = inject(ApplicationService);
  private readonly destroyRef = inject(DestroyRef);

  sidebarItems: SidebarItem[] = []; // top-level items (i.e. parent nodes)
  private features!: FeatureFlags;

  private userCatalogViews!: CatalogViewDto[];
  private mainEmployeeGroups!: EmployeeGroupDto[];
  private invoiceEmployeeGroups!: EmployeeGroupDto[];
  private groupTranslations!: TranslationObject;

  ngOnInit(): void {
    combineLatest([this.accountService.currentUser$, this.accountService.currentUserRoles$])
      .pipe(
        filter(([currentUser, roles]) => !!currentUser && !!roles),
        switchMap(([, roles]) => {
          const isRegistrationPending = roles?.includes('UserRegistrationPending') ?? false;

          // A user still pending registration has no contracts or invoicing access yet, so skip those
          // backend calls (the invoice/contracts menu items are filtered out below regardless).
          return forkJoin({
            userCatalogViews: this.catalogViewService.getUserCatalogViews(),
            employeeGroups: this.employeeService.getUserEmployeeGroups(),
            applicationConfig: this.applicationService.getApplicationConfig(),
            canCloseContracts: isRegistrationPending ? of(false) : this.userContractService.canCloseContracts(),
            isInvoiceMenuAvailable: isRegistrationPending ? of(false) : this.contractService.isInvoiceMenuAvailable(),
          });
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe({
        next: ({ userCatalogViews, employeeGroups, applicationConfig, canCloseContracts, isInvoiceMenuAvailable }) => {
          /**
           * We "deep copy" sidebarData
           * Otherwise when we navigate between different layouts, sidebar items will be persistent (i.e. unwanted ones remain 'open', etc)
           */
          let sidebarList = JSON.parse(JSON.stringify(sidebarData));

          this.userCatalogViews = userCatalogViews;
          this.mainEmployeeGroups = employeeGroups.filter((x) => !x.invoiceGroup);
          this.invoiceEmployeeGroups = employeeGroups.filter((x) => x.invoiceGroup);
          this.features = applicationConfig.features;

          this.groupTranslations = this.translationService.instant('Invoice.ServiceProvider.GroupTranslations');

          if (!canCloseContracts) {
            sidebarList = sidebarList.filter((item: SidebarItem) => item.id !== 'user/contracts');
          }

          if (!isInvoiceMenuAvailable) {
            sidebarList = sidebarList.filter((item: SidebarItem) => item.id !== 'invoice');
          }
          this.sidebarItems = this.createSidebarItemTree([], sidebarList, undefined);
        },
        error: (errorResponse: HttpErrorResponse) => {
          this.errorHandler.displayErrorDialog(errorResponse);
        },
      });
  }

  createSidebarItemTree(
    sidebarItemTree: SidebarItem[],
    sidebarList: SidebarItem[],
    parent: SidebarItem | undefined,
  ): SidebarItem[] {
    for (const item of sidebarList) {
      if (this.isFilteredOut(item)) {
        continue;
      }

      this.attachToParentOrRoot(item, sidebarItemTree, parent);

      if (!this.processSubItems(item, sidebarItemTree)) {
        sidebarItemTree.splice(sidebarItemTree.indexOf(item));
      }
    }

    return sidebarItemTree;
  }

  private isFilteredOut(item: SidebarItem): boolean {
    return (
      (item.id == 'admin/statistics' && !this.features.statistics) ||
      (item.id == 'invoice/terms' && this.features.disableInvoiceTerms)
    );
  }

  private attachToParentOrRoot(item: SidebarItem, tree: SidebarItem[], parent: SidebarItem | undefined): void {
    if (parent) {
      if (!Array.isArray(parent.children)) {
        parent.children = [];
        parent.hasChildren = true;
      }
      if (!parent.children.includes(item)) {
        parent.children.push(item);
        item.parentNode = parent;
      }
    } else {
      item.isFirstLevel = true;
      tree.push(item);
    }
  }

  private processSubItems(item: SidebarItem, sidebarItemTree: SidebarItem[]): boolean {
    switch (item.id) {
      case 'catalogs':
        this.processCatalogs(item, sidebarItemTree);
        return true;
      case 'employee':
        return this.processEmployee(item, sidebarItemTree);
      case 'invoice':
        this.processInvoice(item, sidebarItemTree);
        return true;
      default:
        if (item.subItems) {
          this.createSidebarItemTree(sidebarItemTree, item.subItems, item);
        }
        return true;
    }
  }

  private processCatalogs(item: SidebarItem, sidebarItemTree: SidebarItem[]): void {
    sidebarItemTree.pop(); // remove placeholder item

    for (const { group, catalogViews } of this.getGroupedCatalogViews()) {
      const groupItem = { ...item };
      if (group) {
        groupItem.title = group.title;
        groupItem.translateTitle = false;
      }
      sidebarItemTree.push(groupItem);

      this.createSidebarItemTree(
        sidebarItemTree,
        catalogViews.map((catalogView) => ({
          title: catalogView.title,
          link: `/catalog/management/${catalogView.id}`,
        })),
        groupItem,
      );
    }
  }

  private processEmployee(item: SidebarItem, sidebarItemTree: SidebarItem[]): boolean {
    if (this.mainEmployeeGroups.length == 0) {
      return false;
    }

    const employeeSubItems = this.mainEmployeeGroups.map((employeeGroup) => ({
      title: String(this.groupTranslations[employeeGroup.name] ?? employeeGroup.name),
      link: `/employee/management/${employeeGroup.id}`,
    }));

    this.createSidebarItemTree(sidebarItemTree, employeeSubItems, item);
    return true;
  }

  private processInvoice(item: SidebarItem, sidebarItemTree: SidebarItem[]): void {
    const subItems = item.subItems!;
    let startIndex = subItems.findIndex((x) => x.id === 'user/invoices') + 1;

    for (const employeeGroup of this.invoiceEmployeeGroups) {
      subItems.splice(startIndex, 0, {
        title: String(this.groupTranslations[employeeGroup.name] ?? employeeGroup.name),
        link: `/employee/management/${employeeGroup.id}`,
        icon: 'fa fa-user-doctor',
        requiredRoles: ['Invoice'],
      });
      startIndex++;
    }

    this.createSidebarItemTree(sidebarItemTree, subItems, item);
  }

  private getGroupedCatalogViews() {
    const catalogViewGroups = Object.values(
      groupBy(this.userCatalogViews, (catalogView) => catalogView.catalogViewGroup?.id),
    ).map((catalogViews) => ({
      group: catalogViews[0].catalogViewGroup,
      catalogViews,
    }));

    return sortBy(catalogViewGroups, ({ group }) => group?.order);
  }

  // let's keep track of total open items
  // so we can add '.has-open' to '.sidebar'
  // .has-open class is only needed in some sidebar themes
  // so we can ignore this most of the time
  totalOpen = 0;
  itemToggled($event: SidebarItem) {
    const item = $event;
    if (item.open) this.totalOpen++;
    else this.totalOpen--;

    this.el?.nativeElement.classList.toggle('has-open', this.totalOpen > 0);
  }
}
